/**
 * Job detail island loader: runs when we have static job shell (job-ssr) and job-app.
 * Mounts the interactive JobDetailPage into #job-app, swaps with #job-ssr, then
 * re-renders the tree so charts paint in the now-visible container.
 */
import React from 'react';
import { createRoot } from 'react-dom/client';
import type { Root } from 'react-dom/client';
import { Provider } from 'react-redux';
import { IntlProvider } from 'react-intl';
import { loadLocaleData } from '../../utils/routing/url-helpers';
import { buildFooterStaticStrings } from '../../utils/footer/buildFooterStaticStrings';
import { createStore } from '../../redux/createStore';
import { AuthProvider } from '../../contexts/AuthContext';
import { ToastProvider } from '../../contexts/ToastContext';
import { FooterStringsProvider } from '../../contexts/FooterStringsContext';
import { ToastContainer } from '../../components/Toast';
import { JobIslandProvider } from '../../contexts/JobIslandContext';
import { MinimalChrome } from '../../layouts/MinimalChrome';
import type { PageContextClient } from 'vike/types';
import type { Data } from './@jobId/@seoSlug/+data';

/** Delay before swap; then we re-render again after swap so charts paint when visible. */
const SWAP_DELAY_MS = 100;

let jobRoot: Root | null = null;
let jobRootContainer: HTMLElement | null = null;

function getLocaleFromPathname(pathname: string): string {
  const first = pathname.split('/').filter(Boolean)[0]?.toLowerCase();
  return first && ['en', 'fi', 'sv'].includes(first) ? first : 'en';
}

function runSwap(ssr: HTMLElement, app: HTMLElement): void {
  const wrapper = app.parentElement;
  ssr.style.display = 'none';
  ssr.style.visibility = 'hidden';
  ssr.setAttribute('aria-hidden', 'true');
  ssr.classList.add('job-ssr--replaced');
  if (wrapper) {
    wrapper.style.minHeight = `${wrapper.offsetHeight}px`;
  }
  app.style.visibility = 'visible';
  app.style.opacity = '1';
  app.style.pointerEvents = 'auto';
  app.removeAttribute('aria-hidden');
  app.style.position = 'static';
  if (wrapper) {
    requestAnimationFrame(() => {
      wrapper.style.minHeight = '';
    });
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('resize'));
  }
}

/** Parsed payload: either legacy (data only) or { data, userState } for island hydration. */
interface JobInitialPayload {
  data?: Data;
  userState?: Record<string, unknown>;
  job?: unknown;
}

function parseJobInitial(scriptEl: HTMLScriptElement): { jobData: Data | null; userState: Record<string, unknown> } {
  try {
    const raw = scriptEl.textContent ?? '';
    const parsed = JSON.parse(raw) as JobInitialPayload;
    if (!parsed || typeof parsed !== 'object') {
      return { jobData: null, userState: {} };
    }
    // New shape: { data, userState }
    if (parsed.data && typeof parsed.data === 'object') {
      const data = parsed.data as Data;
      return {
        jobData: data,
        userState: (parsed.userState && typeof parsed.userState === 'object' ? parsed.userState : {}) as Record<string, unknown>,
      };
    }
    // Legacy shape: raw job data (data at top level with .job)
    const asData = parsed as unknown as Data;
    if (asData?.job) {
      return { jobData: asData, userState: {} };
    }
    return { jobData: null, userState: {} };
  } catch {
    return { jobData: null, userState: {} };
  }
}

export function runJobIslandLoader(pageContext: PageContextClient): void {
  const appEl = document.getElementById('job-app');
  const scriptEl = document.getElementById('job-initial');
  if (!appEl || !scriptEl || scriptEl.tagName !== 'SCRIPT') return;

  const { jobData, userState } = parseJobInitial(scriptEl as HTMLScriptElement);
  if (!jobData?.job) {
    appEl.style.visibility = 'visible';
    return;
  }

  const pc = pageContext as { userState?: Record<string, unknown> } | undefined;
  const effectiveUserState = Object.keys(userState).length > 0 ? userState : (pc?.userState && typeof pc.userState === 'object' ? pc.userState : {});

  const pathname =
    typeof window !== 'undefined' ? window.location.pathname : ((pageContext as any)?.urlPathname ?? '/');
  const locale = (pageContext?.locale as string) || getLocaleFromPathname(pathname) || 'en';
  const urlOriginal = typeof window !== 'undefined' ? window.location.href : pathname;
  const messages = loadLocaleData(locale);
  const footerStrings = buildFooterStaticStrings(messages);
  const store = createStore({
    ...pageContext,
    data: jobData,
    userState: effectiveUserState,
    _skipPersist: true,
  } as PageContextClient);
  const jobIslandValue = {
    locale,
    pathname,
    urlOriginal,
    user: (pageContext?.user ?? null) as any,
  };

  // Hidden until swap so we don't paint into a hidden tree; we re-render after swap so charts paint when visible
  appEl.style.visibility = 'visible';
  appEl.style.opacity = '0';
  appEl.style.pointerEvents = 'none';

  const mount = async (): Promise<void> => {
    const { default: JobDetailPage } = await import('./@jobId/@seoSlug/+Page');
    const buildTree = (contentKey: string) =>
      React.createElement(
        Provider,
        { store },
        React.createElement(
          IntlProvider,
          { messages, locale, defaultLocale: 'en' },
          React.createElement(
            FooterStringsProvider,
            { strings: footerStrings },
            React.createElement(
              AuthProvider,
              { initialUser: (pageContext?.user ?? null) as any },
              React.createElement(
                ToastProvider,
                null,
                React.createElement(React.Fragment, null,
                  React.createElement(ToastContainer),
                  React.createElement(
                    JobIslandProvider,
                    { value: jobIslandValue },
                    React.createElement(
                      MinimalChrome,
                      null,
                      React.createElement(JobDetailPage, {
                        key: contentKey,
                        initialData: jobData,
                        initialLocale: locale,
                      })
                    )
                  )
                )
              )
            )
          )
        )
      );
    const tree = buildTree('hidden');

    if (jobRoot && jobRootContainer === appEl) {
      jobRoot.render(tree);
    } else {
      if (jobRoot && jobRootContainer) {
        jobRoot.unmount();
        jobRoot = null;
        jobRootContainer = null;
      }
      jobRoot = createRoot(appEl);
      jobRootContainer = appEl;
      jobRoot.render(tree);
    }

    // Swap then, after a frame so the container is visibly painted, re-render with new key so
    // chart components (including salary insight) remount and paint in the visible container.
    setTimeout(() => {
      const ssr = document.getElementById('job-ssr');
      if (ssr) {
        runSwap(ssr, appEl);
      } else {
        appEl.style.visibility = 'visible';
        appEl.style.opacity = '1';
        appEl.style.pointerEvents = 'auto';
        appEl.removeAttribute('aria-hidden');
        appEl.style.position = 'static';
      }
      const doSecondRender = () => {
        if (jobRoot && jobRootContainer === appEl) {
          jobRoot.render(buildTree('visible'));
        }
      };
      requestAnimationFrame(() => {
        requestAnimationFrame(doSecondRender);
      });
    }, SWAP_DELAY_MS);
  };

  void mount();
}
